<template>
    <loading-indicator v-if="loading" text="Informationen werden abgerufen."/>
    <div v-else-if="enrolments.length" class="message-content fr-view">
        Um fortfahren zu können müssen zunächst folgende Stellenausschreibungen einer anderen Abteilung zugewiesen werden:
        <br>

        <ul>
            <li v-for="enrolment in enrolments">
                <router-link :to="{name: 'enrolment-view', params: {id: enrolment.id}}" target="_blank">{{enrolment.name}}</router-link>
            </li>
        </ul>
    </div>
    <div v-else-if="assignmentSteps.steps.length">
        <app-stepper #default="{step}" :show-last-button="false" :steps="assignmentSteps.steps" :valid-steps="assignmentSteps.valid"
            @change="emitter.emit('emit', {event: 'scroll-top'})">
            <div v-if="step === 'singleEmployees' && singleEmployees.length" key="step_1">
                Um fortzufahren, wählen Sie bitte <u>eine neue Abteilung</u> aus, der anschließend
                <u>alle Mitarbeiter</u> der zu löschenden Abteilung automatisch zugewiesen werden.
                <br>
                <br>
                Falls die Mitarbeiter der zu löschenden Abteilung
                <strong>verschiedenen Abteilungen</strong> zugeordnet werden sollen, ordnen Sie die Mitarbeiter manuell der jeweilig neuen Abteilung zu.

                <div class="row spacer-15">
                    <div class="col-md-6">
                        <form-row label="Alternative Abteilung">
                            <form-select v-model="alternativeEntityId" :options="entitiesSelect" placeholder="Alternative Abteilung wählen"/>
                        </form-row>
                    </div>
                    <div class="col-md-6">
                        <more-less-container>
                            <form-row :label="'Betroffene Mitarbeiter (' + singleEmployees.length + ')'">
                                <person-box-employee v-for="employeeId in singleEmployees" :id="employeeId" :key="employeeId" class="tiny"/>
                            </form-row>
                        </more-less-container>
                    </div>
                </div>
            </div>
            <div v-else-if="step === 'tasks' && assignedTaskCount" key="step_2">
                <entity-assign-tasks v-model="assignTaskData" :assigned-task-count="assignedTaskCount" :entity-id="id" :exclude-entity-id="id"/>
            </div>
        </app-stepper>
    </div>
    <div v-else>
        <div class="row flex-vertical-middle">
            <div class="hidden-xs col-3 text-right">
                <i aria-hidden="true" class="material-icons md-86 text-danger" role="img">delete</i>
            </div>
            <div class="col-12 col-sm-9">
                Sind Sie sicher, dass Sie die Abteilung <strong>{{name}}</strong> löschen möchten?
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import AppStepper from '../../../components/AppStepper.vue';
    import FormRow from '../../../components/form/FormRow.vue';
    import FormSelect from '../../../components/form/FormSelect.vue';
    import FormSelectEmployee from '../../../components/form/FormSelectEmployee.vue';
    import LoadingIndicator from '../../../components/LoadingIndicator.vue';
    import MoreLessContainer from '../../../components/MoreLessContainer.vue';
    import PersonBox from '../../../components/PersonBox.vue';
    import PersonBoxEmployee from '../../../components/PersonBoxEmployee.vue';
    import {getUsername} from '../../../filters/username';
    import {Popup, popupDefaultCancelButton} from '../../../library/popup';
    import TopMessageHandler from '../../../library/topMessageHandler';
    import {errorHandler, XHR_STATUS_DEFAULT} from '../../../utils/portuna';
    import dateHelper from '../../../utils/portuna/dateHelper';
    import EntityAssignTasks from './EntityAssignTasks.vue';

    type TaskAssignData = { owner: number, entity_id: number };

    @Component({
        name: 'EntityDeletePopUp',
        components: {
            EntityAssignTasks,
            FormSelectEmployee,
            AppStepper,
            MoreLessContainer,
            PersonBox,
            PersonBoxEmployee,
            FormSelect,
            FormRow,
            LoadingIndicator,
        },
        methods: {
            dateHelper,
            getUsername,
        },
        emits: ['canceled', 'setData', 'deleted'],
    })
    export default class EntityDeletePopUp extends Vue
    {
        loading = true;

        entitiesSelect: SelectOption[] = [];

        singleEmployees: number[] = [];

        assignedTaskCount: number = 0;

        assignTaskData = {} as TaskAssignData;

        enrolments: {
            id: number
            name: string
        }[] = [];

        alternativeEntityId = 0;

        @Prop({
            type: Number,
            required: true,
        }) id: number;

        @Prop({
            type: String,
            required: true,
        }) name: string;

        get assignmentSteps()
        {
            let data: Record<string, string[]> = {
                steps: [],
                valid: [],
            };

            if (this.singleEmployees.length)
            {
                data.steps.push('singleEmployees');

                if (this.alternativeEntityId)
                {
                    data.valid.push('singleEmployees');
                }
            }

            if (this.assignedTaskCount)
            {
                data.steps.push('tasks');

                if (this.assignTaskData.entity_id || this.assignTaskData.owner)
                {
                    data.valid.push('tasks');
                }
            }

            return data;
        }

        @Watch('alternativeEntityId')
        @Watch('assignTaskData', {deep: true})
        applyButtons()
        {
            let buttons: PopupButton[] = [
                Object.assign(popupDefaultCancelButton, {
                    cmd: (popup: Popup) =>
                    {
                        this.emitter.emit('canceled');
                        popup.close();
                    },
                }),
            ];

            if (!this.enrolments.length)
            {
                buttons.push({
                    type: 'danger',
                    title: 'Abteilung löschen',
                    icon: 'delete',
                    cmd: this.deleteEntity,
                    disabled: !!this.singleEmployees.length && !this.alternativeEntityId
                        || !!this.assignedTaskCount && !this.assignTaskData.entity_id && !this.assignTaskData.owner,
                });
            }

            this.emitter.emit('setButtons', buttons);
        }

        created()
        {
            this.checkAssignments();
        }

        checkAssignments()
        {
            this.loading = true;

            this.axios.get('/employee/entity/delete/id/' + this.id + '/check/1')
            .then(response =>
            {
                if (response.data.status !== XHR_STATUS_DEFAULT)
                {
                    throw response;
                }

                let data               = response.data.data;
                this.entitiesSelect    = data.entities;
                this.singleEmployees   = data.singleEmployees;
                this.enrolments        = data.enrolments;
                this.assignedTaskCount = data.assignedTaskCount;

                if (this.enrolments.length || this.assignedTaskCount || this.singleEmployees.length)
                {
                    this.emitter.emit('setData', {_size: 'medium'});
                }
            })
            .catch(errorHandler)
            .finally(() =>
            {
                this.loading = false;
                this.emitter.emit('lazy-load-focus');
                this.applyButtons();
            });
        }

        deleteEntity()
        {
            this.axios.post(
                '/employee/entity/delete/id/' + this.id,
                {
                    new_entity: this.alternativeEntityId,
                    new_task_owner: this.assignTaskData.owner,
                    new_task_entity_id: this.assignTaskData.entity_id,
                },
            ).then(response =>
            {
                if (response.data.status !== XHR_STATUS_DEFAULT)
                {
                    throw response;
                }

                TopMessageHandler.success(response.data.message);
                this.emitter.emit('deleted');
            })
            .catch(errorHandler);
        }
    };
</script>